/*
 * <bundle-builder> — bundle picker behaviour.
 *
 * Reads its configuration from data attributes:
 *   data-base-id / data-base-qty          → base product variant + qty (the "Shell Only"
 *                                           option; the cheapest variant, locked server-side).
 *   data-bundle-native-id                 → the NATIVE (fixed/componentized) bundle variant.
 *                                           Selecting "bundle" adds just this one variant, which
 *                                           Shopify expands into its components at checkout. Its
 *                                           price is already Markets-converted by Liquid `money`.
 *   data-default                          → "base" | "bundle" (initial selection)
 *   data-cart-url                         → cart page URL to redirect to after adding
 *
 * Selecting an option updates the CTA price. Clicking the CTA adds the matching
 * line item to the cart via /cart/add.js. If the theme has a cart drawer /
 * notification it renders into that (opens the drawer); otherwise it falls back
 * to redirecting to the cart page.
 */
(function () {
  if (window.customElements && customElements.get('bundle-builder')) return;

  // Parse a localized money string (e.g. "$1,234.56", "€1.234,56", "¥1,234")
  // into a Number. Used only to read Shopify's converted reference price, so we
  // can derive the market's exact conversion factor.
  function parseMoney(str) {
    if (!str) return NaN;
    const digits = String(str).replace(/[^0-9.,]/g, '');
    if (!digits) return NaN;
    const lastSep = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
    if (lastSep === -1) return parseFloat(digits);
    const tail = digits.slice(lastSep + 1);
    // A trailing group of 1–2 digits is a decimal separator; anything else
    // (e.g. thousands grouping, or a 0-decimal currency) is not.
    if (tail.length === 1 || tail.length === 2) {
      const intPart = digits.slice(0, lastSep).replace(/[.,]/g, '');
      return parseFloat(intPart + '.' + tail);
    }
    return parseFloat(digits.replace(/[.,]/g, ''));
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.submit = this.querySelector('[data-bundle-submit]');
      this.labelEl = this.querySelector('.bundle-builder__submit-label');
      this.totalEl = this.querySelector('.bundle-builder__total');
      this.errorEl = this.querySelector('.bundle-builder__error');
      this.spinner = this.querySelector('.bundle-builder__spinner');
      this.inputs = Array.from(this.querySelectorAll('.bundle-card__input'));

      this.baseId = this.dataset.baseId;
      this.baseQty = parseInt(this.dataset.baseQty, 10) || 1;
      this.cartUrl = this.dataset.cartUrl || '/cart';

      // Native (fixed / componentized) bundle variant id. The "bundle" option
      // adds THIS single variant (Shopify expands it into its components at
      // checkout); its price is already the native, Markets-converted amount.
      this.nativeId = Number(this.dataset.bundleNativeId) || null;

      // Multi-currency display for the BASE card only: its price/compare are raw
      // base-currency cents (data-money-cents) that we localize on the client.
      // The native bundle card's prices already go through Liquid `money`.
      this.currency = this.resolveCurrency();
      // Base CTA total in raw base-currency cents (so we can convert it for
      // non-base currencies). The bundle total is already localized by `money`.
      this.baseTotalCents = parseInt(
        this.totalEl && this.totalEl.dataset.baseTotalCents,
        10
      );

      // The theme's cart drawer / notification element (Dawn). When present we
      // render into it (open the drawer) instead of redirecting to /cart.
      this.cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      // The theme's headline product price block (Dawn `price` snippet). Selecting
      // a card mirrors that card's already-formatted price/compare into it so the
      // top-of-page price stops looking "stuck" on the bundle price.
      this.priceBlock = document.getElementById('price-' + this.dataset.section);

      // Media switching: each option maps to a media already in the product
      // gallery (a variant's featured media). Selecting a card swaps the hero
      // image via the gallery's setActiveMedia (prepend => shown even when the
      // theme hides non-active variant media).
      this.baseMediaId = this.dataset.baseMediaId || null;
      this.bundleMediaId = this.dataset.bundleMediaId || null;
      this.mediaGallery = document.getElementById('MediaGallery-' + this.dataset.section);

      // Availability (added as separate line items, so the bundle needs both products).
      this.baseAvailable = this.dataset.baseAvailable !== 'false';
      this.bundleAvailable = this.dataset.bundleAvailable === 'true';
      this.soldoutLabel = this.dataset.soldoutLabel || 'Sold out';
      this.defaultLabel =
        (this.labelEl && this.labelEl.getAttribute('data-default-label')) ||
        (this.labelEl && this.labelEl.textContent) ||
        'Add to cart';

      this.inputs.forEach((input) =>
        input.addEventListener('change', () => this.updateState())
      );
      if (this.submit) {
        this.submit.addEventListener('click', () => this.addToCart());
      }

      this.applyCurrency();
      this.updateState();
    }

    // Work out how to convert base-currency amounts to the buyer's active
    // (presentment) currency. Returns { convert, factor, code, base }.
    //
    // The exact factor is derived from a reference price the theme rendered BOTH
    // as raw base cents (data-fx-ref-cents) and through Shopify's `money` filter
    // (data-fx-ref-money) — which Shopify DOES convert under Markets. That gives
    // Shopify's own conversion for this market with no guessing. If the reference
    // didn't meaningfully convert, we leave the server-rendered prices untouched
    // (convert = false) rather than guessing (see resolveCurrency below).
    resolveCurrency() {
      const base = (this.dataset.shopCurrency || '').toUpperCase();
      let code = (this.dataset.activeCurrency || '').toUpperCase();
      const sc = (window.Shopify && window.Shopify.currency) || {};
      if (!code && sc.active) code = String(sc.active).toUpperCase();

      const out = { convert: false, factor: 1, code: code || base, base };
      if (!base || !code || code === base) return out;

      // Derive Shopify's exact conversion factor from the reference price the
      // theme rendered both as raw base cents and through `money` (which Shopify
      // converts under Markets). This is the ONLY source we trust:
      //   - factor meaningfully > 1 or < 1  → Markets converted the reference,
      //     so apply it to the computed amounts.
      //   - factor ≈ 1                      → either the amounts are ALREADY in
      //     the presentment currency, or Markets isn't converting at all. In
      //     both cases converting again would double-convert / mislabel, so we
      //     leave the server-rendered prices untouched (convert stays false).
      // (We deliberately do NOT fall back to window.Shopify.currency.rate: it
      //  can't tell those two cases apart and caused double-conversion.)
      let factor = NaN;
      const refCents = parseInt(this.dataset.fxRefCents, 10);
      const refMoney = parseMoney(this.dataset.fxRefMoney);
      if (isFinite(refCents) && refCents > 0 && isFinite(refMoney) && refMoney > 0) {
        factor = refMoney / (refCents / 100);
      }
      const meaningful = isFinite(factor) && factor > 0 && Math.abs(factor - 1) >= 0.001;
      if (!meaningful) return out;

      out.convert = true;
      out.factor = factor;
      return out;
    }

    // Format a base-currency amount (in cents) into the active currency string.
    formatMoney(baseCents, short) {
      const cents = parseInt(baseCents, 10);
      if (!isFinite(cents)) return '';
      const amount = (cents / 100) * this.currency.factor;
      // Let Intl use each currency's natural decimals (2 for USD/EUR, 0 for
      // JPY/KRW, etc). Only override for the "short" (no-trailing-zeros) style
      // when the amount is whole, mirroring Liquid's money_without_trailing_zeros.
      const dropDecimals = short && Math.round(amount) === amount;
      const opts = { style: 'currency', currency: this.currency.code };
      if (dropDecimals) {
        opts.minimumFractionDigits = 0;
        opts.maximumFractionDigits = 0;
      }
      try {
        return new Intl.NumberFormat(undefined, opts).format(amount);
      } catch (e) {
        // Unknown currency code → last-resort plain number with the code.
        return `${amount.toFixed(dropDecimals ? 0 : 2)} ${this.currency.code}`;
      }
    }

    // Localize every rendered [data-money-cents] amount to the active currency.
    // No-op when the buyer is in the shop's base currency (Liquid already showed
    // the correct amount) or when no conversion factor is available.
    applyCurrency() {
      if (!this.currency.convert) return;
      const nodes = this.querySelectorAll('[data-money-cents]');
      nodes.forEach((node) => {
        const converted = this.formatMoney(
          node.dataset.moneyCents,
          node.dataset.moneyShort === 'true'
        );
        if (converted) node.textContent = converted;
      });
    }

    selectedValue() {
      const checked = this.inputs.find((i) => i.checked);
      return checked ? checked.value : this.dataset.default || 'base';
    }

    // The bundle base is locked to the cheapest variant chosen server-side
    // (data-base-id). We intentionally do NOT follow the page's live variant
    // selection: some products have a pricier "with headphones" variant that
    // already includes the bundled item, and adding it as the base would double
    // that item. Always add the bare (cheapest) variant the snippet rendered.
    currentBaseId() {
      return Number(this.baseId);
    }

    optionAvailable(value) {
      return value === 'bundle' ? this.bundleAvailable : this.baseAvailable;
    }

    updateState() {
      const value = this.selectedValue();
      const available = this.optionAvailable(value);

      if (this.totalEl) {
        let next;
        if (value === 'bundle') {
          // Native bundle total is already Markets-converted by Liquid `money`.
          next = this.totalEl.getAttribute('data-bundle-total');
        } else if (this.currency.convert) {
          // Convert the raw base-currency base total to the buyer's currency.
          next = this.formatMoney(this.baseTotalCents);
        } else {
          // Base currency: use the money string the server already formatted.
          next = this.totalEl.getAttribute('data-base-total');
        }
        // Keep the "– $X" separator that the server rendered.
        this.totalEl.textContent = available && next ? '\u2013\u00a0' + next : '';
      }

      if (this.submit) {
        this.submit.disabled = !available;
      }
      if (this.labelEl) {
        this.labelEl.textContent = available ? this.defaultLabel : this.soldoutLabel;
      }

      this.updatePrice();
      this.updateMedia();
    }

    // Switch the product media gallery to the selected option's image. The image
    // is a variant's featured media that already lives in the gallery, so we just
    // activate it (prepend => it becomes the visible first item under hide_variants).
    updateMedia() {
      const gallery = this.mediaGallery;
      if (!gallery) return;
      if (typeof gallery.setActiveMedia !== 'function') {
        // media-gallery custom element not upgraded yet — retry once.
        if (!this._mediaRetry) {
          this._mediaRetry = true;
          setTimeout(() => this.updateMedia(), 200);
        }
        return;
      }
      const mediaId =
        this.selectedValue() === 'bundle' ? this.bundleMediaId : this.baseMediaId;
      if (!mediaId) return;
      const target = this.dataset.section + '-' + mediaId;
      // Only switch if that media exists in this gallery.
      if (!gallery.querySelector('[data-media-id="' + target + '"]')) return;
      gallery.setActiveMedia(target, true);
    }

    // The <label> card that belongs to the currently-checked radio.
    selectedCard() {
      const checked = this.inputs.find((i) => i.checked);
      if (!checked) return null;
      const next = checked.nextElementSibling;
      if (next && next.classList.contains('bundle-card')) return next;
      return this.querySelector('label[for="' + checked.id + '"]');
    }

    // Mirror the selected card's price + compare-at (already localized by Liquid
    // `money` / applyCurrency) into the theme's headline price block, toggling the
    // sale state + badge so it reads correctly for whichever option is chosen.
    updatePrice() {
      if (!this.priceBlock) return;
      const card = this.selectedCard();
      if (!card) return;

      const priceNode = card.querySelector('.bundle-card__price');
      const compareNode = card.querySelector('.bundle-card__compare');
      const priceStr = priceNode ? priceNode.textContent.trim() : '';
      if (!priceStr) return; // sold-out card has no price to show
      const compareStr = compareNode ? compareNode.textContent.trim() : '';
      const onSale = compareStr.length > 0;

      const container = this.priceBlock.querySelector('.price') || this.priceBlock;
      const set = (sel, text) => {
        const el = this.priceBlock.querySelector(sel);
        if (el) el.textContent = text;
      };

      set('.price__regular .price-item--regular', priceStr);
      set('.price__sale .price-item--sale', priceStr);

      if (onSale) {
        container.classList.add('price--on-sale');
        container.classList.remove('price--sold-out');
        set('.price__sale .price-item--regular', compareStr);
        const p = parseMoney(priceStr);
        const c = parseMoney(compareStr);
        if (isFinite(p) && isFinite(c) && c > 0) {
          const pct = Math.round(((c - p) / c) * 100);
          set('.price__badge-sale', '-' + pct + '%');
        }
      } else {
        container.classList.remove('price--on-sale');
        container.classList.remove('price--sold-out');
      }
    }

    buildItems() {
      const isBundle = this.selectedValue() === 'bundle';
      // Native bundle: add the single bundle variant. Shopify expands it into its
      // components at checkout and prices it natively.
      if (isBundle && this.nativeId) {
        return [{ id: this.nativeId, quantity: 1 }];
      }
      // "Shell Only": add the base (cheapest) variant.
      return [{ id: this.currentBaseId(), quantity: this.baseQty }];
    }

    setLoading(loading) {
      if (!this.submit) return;
      this.submit.disabled = loading;
      if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    async addToCart() {
      if (!this.baseId) return;
      if (!this.optionAvailable(this.selectedValue())) {
        this.showError('This option is sold out.');
        return;
      }
      this.showError('');
      this.setLoading(true);

      const body = { items: this.buildItems() };
      // Ask Shopify to return the rendered cart sections so we can open the
      // drawer (same mechanism Dawn's product-form uses).
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || 'Could not add to cart.');
          this.setLoading(false);
          return;
        }

        // Update the cart count bubble / other subscribers.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-builder',
            productVariantId: this.currentBaseId(),
          });
        }

        if (this.cart && typeof this.cart.renderContents === 'function') {
          if (this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
          this.cart.renderContents(data);
          this.setLoading(false);
        } else {
          // No drawer/notification on this theme — fall back to the cart page.
          window.location.href = this.cartUrl;
        }
      } catch (err) {
        this.showError('Something went wrong. Please try again.');
        this.setLoading(false);
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
